import { Button, Divider, Heading, Skeleton, Text, useToast } from '@afterpaytouch/core'
import { Session } from '@afterpaytouch/portal-api'
import { useTranslation } from 'next-i18next'
import React, { FunctionComponent, useCallback } from 'react'
import { ExtendedPanel } from '../../Panel'
import { Head } from '../../Head'
import { useAmplitudeWithEnduringEventProperties } from '../../../integrations/amplitude'
import { useConsumerIsCashCohort, useDeleteSessionMutation, useGetSessionsQuery } from '../../../state'
import { EditPasswordModal, useEditPasswordModal } from '../../EditPasswordModal'
import { ManagePermissions } from './ManagePermissions'

const I18N_NAMESPACE = ['common', 'account']

// TODO: Add tests
const ManageSessions: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const setToast = useToast()

  const { logEvent } = useAmplitudeWithEnduringEventProperties()

  const {
    data: sessions,
    isFetching: isGetSessionsFetching,
    isLoading: isGetSessionsLoading,
    isError: isGetSessionsError,
    refetch: refetchGetSessionsQuery,
  } = useGetSessionsQuery()

  const [deleteSession] = useDeleteSessionMutation()

  const refetchManageSessions = useCallback((): void => {
    if (isGetSessionsError) {
      refetchGetSessionsQuery()
    }
  }, [isGetSessionsError, refetchGetSessionsQuery])

  const isLoading = isGetSessionsFetching || isGetSessionsLoading
  const hasSessions = typeof sessions !== 'undefined' && !isLoading && !isGetSessionsError && sessions?.length > 0

  const formatLastLoginDate = (lastLogin: Date): string => {
    const locale = 'en-AU'
    const formattedDate = lastLogin.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
    const formattedTime = lastLogin.toLocaleTimeString(locale, {
      hour: 'numeric',
      minute: 'numeric',
      hour12: true,
    })

    return `${formattedDate} at ${formattedTime}`
  }

  const onDeleteSession = async (session: Session): Promise<void> => {
    // @ts-expect-error TODO: fix type error
    logEvent('Clicked remove device')
    try {
      await deleteSession({ params: { id: session.id, type: session.type } }).unwrap()
      setToast({ message: t('account:tab:security:manage:sessions:toast:success'), kind: 'Success', toastTime: 2000, manualDismiss: true })
      // @ts-expect-error TODO: fix type error
      logEvent('Edit device success')
    } catch (error) {
      setToast({ message: t('account:tab:security:manage:sessions:toast:error'), kind: 'Failure', toastTime: 2000, manualDismiss: true })
      // @ts-expect-error TODO: fix type error
      logEvent('Edit device error')
    }
  }

  if (isGetSessionsError) {
    return (
      <>
        <ExtendedPanel.Error onClick={refetchManageSessions} />
        <Divider kind='Hairline' margin='Large' />
      </>
    )
  }

  return (
    <>
      <Heading size='XL'>{t('account:tab:security:manage:sessions:heading')}</Heading>
      <div className='mt-2'>
        <Text>{t('account:tab:security:manage:sessions:description')}</Text>
      </div>
      <div className='mt-6'>
        {!hasSessions && (
          <>
            <Text color='Gray40'>
              {t('account:tab:security:manage:sessions:none')}
              {isLoading && <Skeleton />}
            </Text>
            <Divider kind='Hairline' margin='Large' />
          </>
        )}
        {hasSessions &&
          sessions.map((session) => {
            return (
              <div key={session.id}>
                <div className='flex flex-row justify-between'>
                  <Text bold>
                    {session.device} · {session.browser}
                  </Text>
                  <Button.Link onClick={async () => await onDeleteSession(session)} aria-label={t('account:tab:security:manage:sessions:action')} size='Small'>
                    {t('account:tab:security:manage:sessions:action')}
                  </Button.Link>
                </div>
                <div className='mt-2'>
                  <Text color='Gray40'>{session.location}</Text>
                </div>
                <div className='mt-2'>
                  <Text color='Gray40'>{formatLastLoginDate(new Date(session.lastLoginAt))}</Text>
                </div>
                <Divider kind='Hairline' margin='Large' />
              </div>
            )
          })}
      </div>
    </>
  )
}

// const Timescales = {
//   Year: 'year',
//   Month: 'month',
//   Week: 'week',
//   Day: 'day',
//   Hour: 'hour',
//   Minute: 'minute',
//   Second: 'second',
// } as const
// type TimeScale = typeof Timescales[keyof typeof Timescales]

// type OneOrMany = 'one' | 'many'

// const getPastPresentDiff = (past: Date, present: Date): { scale: TimeScale; oneOrMany: OneOrMany; count: number } => {
//   const seconds = Math.floor((present.getTime() - past.getTime()) / 1000)
//   const ranges: Array<{ scale: TimeScale; limit: number }> = [
//     { scale: Timescales.Year, limit: 31536000 },
//     { scale: Timescales.Month, limit: 2592000 },
//     { scale: Timescales.Week, limit: 604800 },
//     { scale: Timescales.Day, limit: 86400 },
//     { scale: Timescales.Hour, limit: 3600 },
//     { scale: Timescales.Minute, limit: 60 },
//   ]

//   for (const r of ranges) {
//     const interval = Math.floor(seconds / r.limit)
//     if (interval === 1) {
//       return { scale: r.scale, oneOrMany: 'one', count: interval }
//     }
//     if (interval > 1) {
//       return { scale: r.scale, oneOrMany: 'many', count: interval }
//     }
//   }

//   return { scale: Timescales.Second, oneOrMany: Math.floor(seconds) === 1 ? 'one' : 'many', count: Math.floor(seconds) }
// }

// const useLastUpdated = ({ scale, oneOrMany, count }: ReturnType<typeof getPastPresentDiff>): string => {
//   const { t } = useTranslation(I18N_NAMESPACE)
//   if (scale === 'second') return t('common:lastUpdated:now')
//   return t(`common:lastUpdated:${scale}:${oneOrMany}`, { count })
// }

const ManagePassword: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)

  const [isEditPasswordModalOpen, { openModal: openEditPasswordModal, closeModal: closeEditPasswordModal }] = useEditPasswordModal()

  const isLoading = false

  /* TODO: Where does dateLastUpdated come from? */
  // const dateLastUpdated = new Date('2022-05-18 01:15:00 GMT+1000')
  // const dateNow = new Date()
  // const lastUpdated = useLastUpdated(getPastPresentDiff(dateLastUpdated, dateNow))

  return (
    <>
      <div className='relative flex flex-row justify-between'>
        <Heading size='XL'>{t('account:tab:security:manage:password:heading')}</Heading>
        <Button.Link onClick={openEditPasswordModal} aria-label='edit' size='Small'>
          {t('account:tab:security:manage:password:action')}
        </Button.Link>
        {isLoading && <Skeleton />}
      </div>
      <Divider kind='Hairline' margin='Large' />
      <EditPasswordModal isModalOpen={isEditPasswordModalOpen} closeModal={closeEditPasswordModal} />
    </>
    // TODO: Use this once password last updated is returned from Portal API
    // <>
    //   <Heading size='XL'>{t('account:tab:security:manage:password:heading')}</Heading>
    //   <div className='relative mt-2 flex flex-row justify-between'>
    //     <Text color='Gray40'>{lastUpdated}</Text>
    //     <Button.Link onClick={openEditPasswordModal} aria-label='edit' size='Small'>
    //       {t('account:tab:security:manage:password:action')}
    //     </Button.Link>
    //     {isLoading && <Skeleton />}
    //   </div>
    //   <Divider kind='Hairline' margin='Large' />
    //   <EditPasswordModal isModalOpen={isEditPasswordModalOpen} closeModal={closeEditPasswordModal} />
    // </>
  )
}

export const SecurityTab: FunctionComponent = () => {
  const { t } = useTranslation(I18N_NAMESPACE)
  const isCashCohort = useConsumerIsCashCohort()

  const pageTitle = `${t('account:tab:security:title')} - ${t('account:title')} | ${t('common:brandName')}`

  return (
    <>
      <Head title={pageTitle} />
      <div className='max-w-[528px]'>
        {!isCashCohort && (
          <div>
            <ManagePassword />
          </div>
        )}
        <div className={isCashCohort ? 'mb-4' : 'mb-4 mt-10'}>
          <ManageSessions />
        </div>
        <div className='mt-10'>
          <ManagePermissions />
        </div>
      </div>
    </>
  )
}
